/**
 * Identity for the client: a device token minted on first visit, a guest account created
 * from it, and an in-place upgrade to a full account.
 *
 * The JWT this holds is what `Hello.token` carries; everything else (stash, vendor,
 * feedback) sends it as `Authorization: Bearer`. If the auth endpoints are unreachable the
 * session drops to `offline`: no token, no stash. A test server without an authenticator
 * still admits the player; the production server kicks an unverifiable Hello with 'auth',
 * which main.ts answers with one `renew()` + reconnect before giving up.
 */

import { ApiError, api, type AuthResponse, type PlayerProfile } from './api.js';

const DEVICE_KEY = 'stillsun.device';
const TOKEN_KEY = 'stillsun.token';
const NAME_KEY = 'stillsun.name';

export type SessionStatus = 'anonymous' | 'guest' | 'full' | 'offline';

export interface SessionState {
  status: SessionStatus;
  token: string;
  player: PlayerProfile | null;
  /** Human-readable reason the last auth attempt failed, or ''. */
  error: string;
  busy: boolean;
}

function readLocal(key: string): string {
  try {
    return localStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
}

function writeLocal(key: string, value: string): void {
  try {
    if (value.length === 0) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* private mode */
  }
}

/** 32 random bytes as hex. Stable for the life of the browser profile. */
export function deviceToken(): string {
  const existing = readLocal(DEVICE_KEY);
  if (/^[0-9a-f]{64}$/.test(existing)) return existing;
  const bytes = new Uint8Array(32);
  try {
    crypto.getRandomValues(bytes);
  } catch {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  let hex = '';
  for (const b of bytes) hex += b.toString(16).padStart(2, '0');
  writeLocal(DEVICE_KEY, hex);
  return hex;
}

/** Roles that get a badge in the hub. */
export const ROLE_BADGE: Record<string, { label: string; color: string }> = {
  founder: { label: 'FOUNDER', color: '#9ff0c3' },
  admin: { label: 'ADMIN', color: '#7f9bff' },
  owner: { label: 'OWNER', color: '#c9bcff' },
};

type Listener = (s: SessionState) => void;

export class Session {
  private state: SessionState = {
    status: 'anonymous',
    token: readLocal(TOKEN_KEY),
    player: null,
    error: '',
    busy: false,
  };
  private readonly listeners = new Set<Listener>();

  get current(): SessionState {
    return this.state;
  }
  get token(): string {
    return this.state.token;
  }
  get player(): PlayerProfile | null {
    return this.state.player;
  }
  get name(): string {
    return this.state.player?.name ?? readLocal(NAME_KEY) ?? 'drifter';
  }
  get role(): string {
    return this.state.player?.role ?? 'player';
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.state);
    return () => {
      this.listeners.delete(fn);
    };
  }

  private set(patch: Partial<SessionState>): void {
    this.state = { ...this.state, ...patch };
    for (const fn of [...this.listeners]) {
      try {
        fn(this.state);
      } catch (err) {
        console.error('[stillsun] session listener threw', err);
      }
    }
  }

  saveName(name: string): void {
    writeLocal(NAME_KEY, name);
  }
  loadName(): string {
    return readLocal(NAME_KEY);
  }

  private adopt(res: AuthResponse, status: SessionStatus): void {
    writeLocal(TOKEN_KEY, res.token);
    if (res.player.name.length > 0) this.saveName(res.player.name);
    this.set({ status, token: res.token, player: res.player, error: '', busy: false });
  }

  /**
   * Boot path. Reuses a stored JWT when `/api/me` still accepts it, otherwise mints a
   * guest from the device token. Never throws: a dead backend leaves us 'offline'.
   */
  async boot(name: string): Promise<SessionState> {
    this.set({ busy: true, error: '' });
    const stored = this.state.token;
    if (stored.length > 0) {
      try {
        const me = await api<PlayerProfile | { player: PlayerProfile }>('/api/me', {
          token: stored,
        });
        const player = 'player' in me ? me.player : me;
        this.saveName(player.name);
        this.set({
          status: player.kind === 'full' ? 'full' : 'guest',
          player,
          error: '',
          busy: false,
        });
        return this.state;
      } catch (err) {
        if (err instanceof ApiError && err.missing) {
          this.set({ status: 'offline', player: null, busy: false, error: '' });
          return this.state;
        }
        // 401 / expired: fall through and mint a fresh guest.
        writeLocal(TOKEN_KEY, '');
        this.set({ token: '' });
      }
    }
    return this.guest(name);
  }

  async guest(name: string): Promise<SessionState> {
    this.set({ busy: true, error: '' });
    try {
      const res = await api<AuthResponse>('/api/auth/guest', {
        body: { deviceToken: deviceToken(), name: name.slice(0, 20) },
      });
      this.adopt(res, 'guest');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'auth failed';
      const missing = err instanceof ApiError && err.missing;
      this.set({
        status: 'offline',
        player: null,
        busy: false,
        error: missing ? '' : msg,
      });
      if (!missing) console.warn('[stillsun] guest auth failed', err);
    }
    return this.state;
  }

  /** Upgrade the current guest in place. Throws with a readable message on failure. */
  async register(email: string, password: string): Promise<void> {
    this.set({ busy: true, error: '' });
    try {
      const res = await api<AuthResponse | { token: string }>('/api/auth/register', {
        body: { email, password },
        token: this.state.token,
      });
      if ('player' in res) this.adopt(res, 'full');
      else {
        writeLocal(TOKEN_KEY, res.token);
        this.set({ status: 'full', token: res.token, busy: false, error: '' });
        await this.refresh();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'register failed';
      this.set({ busy: false, error: msg });
      throw err;
    }
  }

  async login(email: string, password: string): Promise<void> {
    this.set({ busy: true, error: '' });
    try {
      const res = await api<AuthResponse | { token: string }>('/api/auth/login', {
        body: { email, password },
      });
      if ('player' in res) this.adopt(res, 'full');
      else {
        writeLocal(TOKEN_KEY, res.token);
        this.set({ status: 'full', token: res.token, busy: false, error: '' });
        await this.refresh();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'login failed';
      this.set({ busy: false, error: msg });
      throw err;
    }
  }

  /** Re-reads the profile (wick/level change after a raid or a vendor trade). */
  async refresh(): Promise<void> {
    if (this.state.token.length === 0) return;
    try {
      const me = await api<PlayerProfile | { player: PlayerProfile }>('/api/me', {
        token: this.state.token,
      });
      const player = 'player' in me ? me.player : me;
      this.set({ player, status: player.kind === 'full' ? 'full' : 'guest' });
    } catch {
      /* keep the stale profile; the hub still works */
    }
  }

  signOut(): void {
    writeLocal(TOKEN_KEY, '');
    this.set({ status: 'anonymous', token: '', player: null, error: '' });
  }

  /**
   * The server refused our token at Hello (`Kick 'auth'`): it may be stale, minted under a
   * rotated secret, or empty because boot ran while the API was unreachable. Drop it and mint
   * a fresh guest from the same device token. Resolves true when a usable token is in hand.
   * A full account cannot be renewed this way; the player has to log in again.
   */
  async renew(name: string): Promise<boolean> {
    if (this.state.status === 'full') {
      this.signOut();
      this.set({ error: 'session expired — log in again' });
      return false;
    }
    writeLocal(TOKEN_KEY, '');
    this.set({ token: '' });
    const state = await this.guest(name);
    return state.token.length > 0;
  }

  /**
   * Character creation renamed this account, so the JWT we are holding is stale.
   *
   * The name is baked into the token and the room reads it from there, not from the database —
   * so without this the player would walk into Lanternfall under whatever name the guest
   * account was minted with. Not an `adopt`: there is no `AuthResponse` here, the status is
   * unchanged, and the profile is the one we already had with a new name on it.
   */
  adoptCharacter(token: string, name: string): void {
    writeLocal(TOKEN_KEY, token);
    this.saveName(name);
    const player = this.state.player;
    this.set({ token, player: player === null ? null : { ...player, name }, error: '' });
  }

  clearError(): void {
    if (this.state.error.length > 0) this.set({ error: '' });
  }
}

export const session = new Session();
